import Decimal from 'decimal.js';
import {CoverageType, Field, Harvest, YieldValue} from '../models/interfaces';
import {PriceAreaValue} from '../models/types';
import {convertArea, derivePriceAreaUnit, deriveUnitPriceUnit} from '../selectors/units';
import {aggregate} from './arr-util';

// IMPORTANT: do not use this function anymore, as it is only relevant for the policies issued until 2025-06-16, which
// didn't use rounding to 2 decimals.
export function calculatedInsuredYieldPriorTo20250616(
  referenceYield: null | YieldValue,
  insuredPercent: null | number,
) {
  return new Decimal(referenceYield?.val ?? 0).mul(new Decimal(insuredPercent ?? 0).div(100)).toNumber();
}

export function calculateInsuredYield(referenceYield: null | YieldValue, insuredPercent: null | number) {
  return new Decimal(referenceYield?.val ?? 0)
    .mul(new Decimal(insuredPercent ?? 0).div(100))
    .toDecimalPlaces(2)
    .toNumber();
}

export function calculateCommodityPrice(
  referenceYield: Harvest['reference_yield'],
  sumInsuredPerArea: null | PriceAreaValue,
  insuredYield: number,
) {
  return sumInsuredPerArea && insuredYield && referenceYield
    ? {
        val: Number((sumInsuredPerArea.val / insuredYield).toFixed(2)),
        unit: deriveUnitPriceUnit(sumInsuredPerArea.unit, referenceYield.unit),
      }
    : null;
}

// The function assumes that the insuredYield has the same unit as reference yield.
export function calculateSumInsuredPerArea(
  referenceYield: Harvest['reference_yield'],
  commodityPrice: Harvest['commodity_price'],
  insuredYield: number,
) {
  return commodityPrice && referenceYield
    ? {
        val: Number((insuredYield * commodityPrice.val).toFixed(2)),
        unit: derivePriceAreaUnit(commodityPrice.unit, referenceYield.unit),
      }
    : null;
}

// Replanting sum insured is defined as a percent of total sum insured, which we currently hardcode to 25%.
const replantingSumInsuredPercent = 25;

export function calculatePaymentSlipAmount(
  installmentIndex: number,
  premiumPaidByInsured: number,
  installmentCount: number,
): number {
  if (installmentIndex >= installmentCount) {
    throw new Error(
      'calculatePaymentSlipAmount: payment slip index must never equal or be higher than installment count!',
    );
  }
  const premium = new Decimal(premiumPaidByInsured);
  if (installmentCount === 1) {
    return premium.toNumber();
  }
  const baseInstallment = premium.div(installmentCount);
  const roundedInstallment = baseInstallment.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  if (installmentIndex === 0) {
    // When installment amounts are divided on equal parts, there may be infinite number of digits after the decimal
    // point. Based on customer feedback on 2025-05-19, these digits must be added or subtracted in the first installment.
    // The following calculation ensures that this value is added (or subtracted) to the first installment.
    // View test cases in server/src/worker/test_processAxaIssuePaymentSlips.ts:21.
    return baseInstallment.plus(baseInstallment.minus(roundedInstallment).mul(installmentCount - 1)).toNumber();
  } else {
    return roundedInstallment.toNumber();
  }
}

export function calculateSubsidy(premium: number, subsidyPercent: number) {
  return new Decimal(premium).mul(new Decimal(subsidyPercent).div(100)).toDecimalPlaces(2).toNumber();
}

// TODO(kristjan): refactor the interface to use only Decimal until the last moment the value is actually needed.
interface PolicyPremiumDetails {
  areaHa: number;
  basicSumInsured: number;
  basicPremium: number;
  replantingSumInsured: number;
  replantingPremium: number;
  totalPremium: number;
  // Note that the premium details object may represent the component (field/polygon) and not the whole policy and as
  // such the subsidy below will also represent the appropriate percentages of that component that is subsidized.
  federalSubsidy: number;
  stateSubsidy: number;
  premiumPaidByInsured: number;
}

export function calculatePremiumDetails(
  selectedHarvests: Harvest[],
  fields: Field[],
  coveragePerArea: PriceAreaValue,
  premiumRatePercent: number,
  replantingPremiumRatePercent: number,
  coverageType: CoverageType,
  federalSubsidyPercent: number,
  stateSubsidyPercent: number,
): PolicyPremiumDetails {
  function getTotalFieldLevelHarvestsAreaHa(selectedFieldHarvests: Harvest[], fields: Field[]): number {
    return (
      selectedFieldHarvests
        ?.map(h => {
          return h.harvest_area ?? fields.find(f => f.field_id === h.field_id)?.field_area ?? null;
        })
        .map(area => convertArea('hectares', area)?.val ?? null)
        .reduce(...aggregate.sum) ?? 0
    );
  }
  // Note that total sum insured is limited to the basic sum insured, as this will be the maximum indemnity limit.
  // This means that if there is a total loss in replanting the remaining amount that could be paid for the
  // basic coverage will be basic sum insured - replanting sum insured.
  function calculateBasicSumInsured(sumInsuredPerArea: PriceAreaValue, selectedFieldsHarvestAreaHa: number) {
    return new Decimal(sumInsuredPerArea.val).mul(selectedFieldsHarvestAreaHa).toDecimalPlaces(2).toNumber();
  }
  // Premium rate percent is expected to be 0-100 (as all percent variables in GT).
  function calculateBasicPremium(sumInsured: number, premiumRatePercent: number) {
    return new Decimal(sumInsured).mul(premiumRatePercent).div(100).toDecimalPlaces(2).toNumber();
  }
  function calculateReplantingSumInsured(sumInsured: number, coverageType: CoverageType) {
    return coverageType === 'replanting'
      ? new Decimal(sumInsured)
          .mul(replantingSumInsuredPercent / 100)
          .toDecimalPlaces(2)
          .toNumber()
      : 0;
  }
  // Replanting premium rate percent is expected to be 0-100 (as all percent variables in GT).
  function calculateReplantingPremium(
    basicSumInsured: number,
    replantingPremiumRatePercent: number,
    coverageType: CoverageType,
  ) {
    return coverageType === 'replanting'
      ? new Decimal(basicSumInsured).mul(replantingPremiumRatePercent).div(100).toDecimalPlaces(2).toNumber()
      : 0;
  }
  function calculateTotalPremium(basic: number, replanting: number) {
    return new Decimal(basic).plus(replanting).toDecimalPlaces(2).toNumber();
  }
  const areaHa = getTotalFieldLevelHarvestsAreaHa(selectedHarvests, fields);
  const basicSumInsured = calculateBasicSumInsured(coveragePerArea, areaHa);
  const basicPremium = calculateBasicPremium(basicSumInsured, premiumRatePercent);
  const replantingSumInsured = calculateReplantingSumInsured(basicSumInsured, coverageType);
  const replantingPremium = calculateReplantingPremium(basicSumInsured, replantingPremiumRatePercent, coverageType);
  const totalPremium = calculateTotalPremium(basicPremium, replantingPremium);
  const federalSubsidy = calculateSubsidy(totalPremium, federalSubsidyPercent);
  const stateSubsidy = calculateSubsidy(totalPremium, stateSubsidyPercent);
  return {
    areaHa,
    basicSumInsured,
    basicPremium,
    replantingSumInsured,
    replantingPremium,
    totalPremium,
    federalSubsidy,
    stateSubsidy,
    premiumPaidByInsured: new Decimal(totalPremium)
      .minus(federalSubsidy)
      .minus(stateSubsidy)
      .toDecimalPlaces(2)
      .toNumber(),
  };
}
